import { useCallback, useEffect, useRef, useState } from "react";
import {
  Upload,
  Search,
  FileText,
  FileSpreadsheet,
  FileImage,
  Trash2,
  Pencil,
  Lock,
  ExternalLink,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Page, PageHeader, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, API, errMsg } from "@/lib/api";
import { PrintButton } from "@/components/PrintButton";
import { printDetailSheet } from "@/lib/print";

export const DOC_CATEGORIES = [
  "Practical instructions",
  "Risk assessment",
  "Technician notes",
  "Worksheet",
  "Department procedure",
  "Equipment manual",
  "Chemical safety",
  "Timetable",
  "Other",
];
export const OPERATIONAL_DOC_CATEGORIES = ["Invoice", "Delivery note", "Instructions"];
const NEW_CATEGORY = "__new_category";

function DocumentCategoryPicker({ value, categories, onChange }) {
  const [creating, setCreating] = useState(false);
  const customCategory = creating || (value && !categories.includes(value));
  const selected = customCategory ? NEW_CATEGORY : value;
  return (
    <div className="space-y-2">
      <Select
        value={selected}
        onValueChange={(nextValue) => {
          if (nextValue === NEW_CATEGORY) {
            setCreating(true);
            onChange("");
            return;
          }
          setCreating(false);
          onChange(nextValue);
        }}
      >
        <SelectTrigger data-testid="document-category-select">
          <SelectValue placeholder="Choose category" />
        </SelectTrigger>
        <SelectContent>
          {categories.map((category) => (
            <SelectItem key={category} value={category}>
              {category}
            </SelectItem>
          ))}
          <SelectItem value={NEW_CATEGORY}>Add a new category…</SelectItem>
        </SelectContent>
      </Select>
      {customCategory && (
        <Input
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          placeholder="New category name"
          data-testid="document-new-category-input"
        />
      )}
    </div>
  );
}

const iconFor = (name) =>
  /\.(xlsx?|csv)$/i.test(name)
    ? FileSpreadsheet
    : /\.(png|jpe?g|webp)$/i.test(name)
      ? FileImage
      : FileText;
const sizeText = (n) =>
  n > 1048576
    ? `${(n / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;

function Pick({ value, onChange, options, testId, labels = {} }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {labels[o] || o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const VIS_LABELS = { all: "All staff", prep: "Technicians and admins only" };

function DocDialog({ doc, categories, onClose, onSaved, operationalOnly = false }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({
    title: doc?.title || "",
    category: doc?.category || (operationalOnly ? "Invoice" : "Practical instructions"),
    description: doc?.description || "",
    visibility: doc?.visibility || "all",
    supplier_tag: doc?.supplier_tag || "",
    equipment_tag: doc?.equipment_tag || "",
    order_tag: doc?.order_tag || "",
    operational: doc?.operational || operationalOnly,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v }));
  const isOperational = f.operational || OPERATIONAL_DOC_CATEGORIES.includes(f.category);
  const save = async () => {
    setBusy(true);
    try {
      if (doc) {
        await api.put(`/documents/${doc.id}`, f);
        toast.success("Document updated");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(f).forEach(([k, v]) => fd.append(k, v));
        const { data } = await api.post("/documents", fd);
        toast.success(
          data.index_status === "indexed"
            ? `Uploaded and indexed (${data.chunks} sections) — Jonny can now use it`
            : "Uploaded, but no readable text was found to index",
        );
      }
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="document-dialog">
        <DialogHeader>
          <DialogTitle>
            {doc ? "Edit document" : "Upload a resource"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!doc && (
            <div>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const x = e.target.files?.[0];
                  setFile(x || null);
                  if (x && !f.title)
                    set("title")(x.name.replace(/\.[^.]+$/, ""));
                }}
                data-testid="document-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full rounded-xl border-2 border-dashed p-6 text-sm text-muted-foreground hover:border-primary/60"
                data-testid="document-file-button"
              >
                {file ? (
                  <span className="text-foreground font-medium">
                    {file.name} · {sizeText(file.size)}
                  </span>
                ) : (
                  "Choose a PDF, Word, Excel, CSV, text file or image (max 15 MB)"
                )}
              </button>
            </div>
          )}
          <div className="space-y-1">
            <Label>Title</Label>
            <Input
              value={f.title}
              onChange={(e) => set("title")(e.target.value)}
              data-testid="document-title-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Type</Label>
              <DocumentCategoryPicker
                value={f.category}
                onChange={set("category")}
                categories={categories}
              />
            </div>
            {isOperational ? (
              <div className="space-y-1">
                <Label>Who can see it</Label>
                <p
                  className="h-10 flex items-center rounded-md border bg-muted px-3 text-sm text-muted-foreground"
                  data-testid="document-operational-visibility"
                >
                  Technicians and admins only
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Who can see it</Label>
                <Pick
                  value={f.visibility}
                  onChange={set("visibility")}
                  options={["all", "prep"]}
                  labels={VIS_LABELS}
                  testId="document-visibility-select"
                />
              </div>
            )}
          </div>
          {isOperational && (
            <div
              className="grid gap-3 sm:grid-cols-3"
              data-testid="document-operational-tags"
            >
              <div className="space-y-1">
                <Label>Supplier</Label>
                <Input
                  value={f.supplier_tag}
                  onChange={(e) => set("supplier_tag")(e.target.value)}
                  data-testid="document-supplier-tag-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Equipment</Label>
                <Input
                  value={f.equipment_tag}
                  onChange={(e) => set("equipment_tag")(e.target.value)}
                  data-testid="document-equipment-tag-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Order</Label>
                <Input
                  value={f.order_tag}
                  onChange={(e) => set("order_tag")(e.target.value)}
                  data-testid="document-order-tag-input"
                />
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label>Description (optional)</Label>
            <Textarea
              rows={2}
              value={f.description}
              onChange={(e) => set("description")(e.target.value)}
              data-testid="document-description-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={busy || (!doc && !file) || !f.title.trim()}
            data-testid="document-save-button"
          >
            {busy
              ? doc
                ? "Saving…"
                : "Uploading and indexing…"
              : doc
                ? "Save"
                : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocRow({ d, snippet, canChange, onEdit, onDelete }) {
  const Icon = iconFor(d.filename);
  return (
    <div
      className="flex items-start gap-4 px-4 py-3"
      data-testid={`document-row-${d.id}`}
    >
      <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <a
          href={`${API}/documents/${d.id}/file`}
          target="_blank"
          rel="noreferrer"
          className="font-medium hover:underline inline-flex items-center gap-1.5"
          data-testid={`document-open-${d.id}`}
        >
          {d.title}
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
        </a>
        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
          <span>{d.category}</span>
          <span>
            {d.filename} · {sizeText(d.size)}
          </span>
          <span>{d.uploaded_by}</span>
          <span>
            {new Date(d.created_at).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </span>
          {d.visibility === "prep" && (
            <span className="inline-flex items-center gap-1">
              <Lock className="h-3 w-3" />
              Technicians and admins
            </span>
          )}
          {d.index_status !== "indexed" && (
            <span
              className="text-amber-600"
              data-testid={`document-unindexed-${d.id}`}
            >
              Not searchable (no readable text)
            </span>
          )}
        </div>
        {d.description && <p className="text-sm mt-1">{d.description}</p>}
        {(d.supplier_tag || d.equipment_tag || d.order_tag) && (
          <p
            className="text-xs mt-1 text-muted-foreground"
            data-testid={`document-tags-${d.id}`}
          >
            {[
              d.supplier_tag && `Supplier: ${d.supplier_tag}`,
              d.equipment_tag && `Equipment: ${d.equipment_tag}`,
              d.order_tag && `Order: ${d.order_tag}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        {snippet && (
          <p
            className="text-xs mt-1.5 rounded-md bg-muted px-2 py-1.5 line-clamp-3"
            data-testid={`document-snippet-${d.id}`}
          >
            …{snippet}…
          </p>
        )}
      </div>
      <div className="flex gap-1 print-hidden">
        <Button
          size="icon"
          variant="ghost"
          onClick={() => printDetailSheet(d.title, [
            { label: "Type", value: d.category },
            { label: "File", value: d.filename },
            { label: "Size", value: sizeText(d.size) },
            { label: "Uploaded", value: new Date(d.created_at).toLocaleDateString("en-GB") },
            { label: "Description", value: d.description },
            { label: "Supplier", value: d.supplier_tag },
            { label: "Equipment", value: d.equipment_tag },
            { label: "Order", value: d.order_tag },
          ])}
          aria-label={`Print ${d.title}`}
          data-testid={`document-print-${d.id}`}
        >
          <Printer className="h-4 w-4" />
        </Button>
        {canChange && (
          <>
          <Button
            size="icon"
            variant="ghost"
            onClick={onEdit}
            data-testid={`document-edit-${d.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={onDelete}
            data-testid={`document-delete-${d.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function DocumentsPage({ operationalOnly = false }) {
  const { user, can } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ documents: [], matches: {} });
  const [dialog, setDialog] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => {
    api
      .get("/documents", {
        params: {
          q,
          category: cat === "all" ? "" : cat,
          operational_only: operationalOnly,
        },
      })
      .then((r) => setData(r.data));
  }, [q, cat, operationalOnly]);
  const loadCategories = useCallback(() => {
    api
      .get("/documents/categories", { params: { operational_only: operationalOnly } })
      .then((response) => setCategories(response.data))
      .catch((error) => toast.error(errMsg(error)));
  }, [operationalOnly]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);
  const remove = async () => {
    try {
      await api.delete(`/documents/${del.id}`);
      toast.success("Document deleted");
      setDel(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const docs = data.documents;
  return (
    <Page wide>
      <PageHeader
        title={operationalOnly ? "Documents" : "Resources"}
        subtitle={`${docs.length} document${docs.length === 1 ? "" : "s"} · Jonny can search these when answering`}
      >
        <PrintButton label="Print current list" testId="documents-print-button" />
        {can("documents:upload") && (
          <Button
            onClick={() => setDialog({})}
            data-testid="document-upload-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        )}
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search inside documents…"
            className="pl-9"
            data-testid="document-search-input"
          />
        </div>
        <div className="sm:w-56">
          <Pick
            value={cat}
            onChange={setCat}
            options={["all", ...categories]}
            labels={{ all: "All types" }}
            testId="document-category-filter"
          />
        </div>
      </div>
      {docs.length === 0 ? (
        <Empty>
          {q || cat !== "all"
            ? "No documents match."
            : "No resources yet. Upload risk assessments, technician notes, worksheets or safety sheets so Jonny can use them."}
        </Empty>
      ) : (
        <div
          className="rounded-xl border bg-card divide-y"
          data-testid="documents-list"
        >
          {docs.map((d) => (
            <DocRow
              key={d.id}
              d={d}
              snippet={data.matches[d.id]}
              canChange={
                d.uploaded_by_id === user.id || can("documents:manage")
              }
              onEdit={() => setDialog({ doc: d })}
              onDelete={() => setDel(d)}
            />
          ))}
        </div>
      )}
      {dialog && (
        <DocDialog
          doc={dialog.doc}
          categories={categories}
          operationalOnly={operationalOnly}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            load();
            loadCategories();
          }}
        />
      )}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="document-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{del?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The file and its search index are removed. Jonny will no longer
              use it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              data-testid="document-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
